/**
 * AppModeContext — Manages Admin ↔ Portal switching for admin users with MA link.
 *
 * Provides:
 * - mode: 'admin' | 'portal'
 * - setMode(mode): Switch between admin and portal
 * - canSwitch: true if user is admin AND has mitarbeiter_id
 * - ready: true once user is loaded and mode is resolved (prevents flash of wrong sidebar)
 *
 * @since v2.283.0
 * @updated v2.290.1 — ready guard to prevent admin/portal sidebar mix on hard refresh
 */
import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const AppModeContext = createContext({ mode: 'admin', setMode: () => {}, canSwitch: false, ready: false });

export function AppModeProvider({ children }) {
  const { user } = useAuth();

  // Start in admin mode — only restore portal after user is confirmed
  const [mode, setModeState] = useState('admin');

  // ready = true once we've resolved the mode based on actual user data
  const ready = !!user;

  // Determine if user can switch — jeder interne Admin-Layout-User mit MA-Verknüpfung
  // darf in SEIN eigenes MA-Portal (Backend portalAuth prüft eigene mitarbeiter_id, #1270-Muster).
  const isInternal = user && !['portal_kunde', 'portal_mitarbeiter', 'jv_partner', 'kunde'].includes(user.rolle);
  const hasMaLink = user && user.mitarbeiter_id;
  const canSwitch = !!(isInternal && hasMaLink);

  // Restore stored mode ONLY after user is known and canSwitch is determined
  useEffect(() => {
    if (!user) return;
    try {
      const stored = localStorage.getItem('weship_app_mode');
      if (stored === 'portal' && canSwitch) {
        setModeState('portal');
      } else {
        setModeState('admin');
        if (stored) localStorage.removeItem('weship_app_mode');
      }
    } catch { setModeState('admin'); }
  }, [user, canSwitch]);

  const setMode = (newMode) => {
    if (newMode === 'portal' && !canSwitch) return;
    setModeState(newMode);
    try {
      if (newMode === 'admin') localStorage.removeItem('weship_app_mode');
      else localStorage.setItem('weship_app_mode', newMode);
    } catch {}
  };

  return (
    <AppModeContext.Provider value={{ mode, setMode, canSwitch, ready }}>
      {children}
    </AppModeContext.Provider>
  );
}

export function useAppMode() {
  return useContext(AppModeContext);
}
